import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import ProduccionGuiaPdfButton from '../../../components/Produccion/ProduccionGuiaPdfButton';
import { useCanMutate } from '../../../hooks/useCanMutate';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import recetaPlatoService from '../../../services/recetaPlatoService';
import entryGuideService from '../../../services/entryGuideService';
import produccionGuiaService from '../../../services/produccionGuiaService';
import { productService } from '../../../services/productService';
import { getGuiaConfigBySlug } from '../../../utils/produccionGuiaPaths';
import { lineasInsumoEscaladosRecetaApi } from '../../../data/mockSalidasProduccion';
import {
  applyKardexToInsumoResumen,
  buildDesgloseInsumoPorReceta,
  buildExplosionPepAnalisis,
  applyCoproductosToInsumoResumen,
  buildGenerandoseByProductId,
  buildResumenByproductsFromInsumos,
  buildResumenInsumosGuia,
  buildResumenPlatosGuia,
  insumoSinStockSuficiente,
  stockDisponibleInsumoGuia,
  formatearCantidadReceta4Dec,
  formatearCostoUnitario4Dec,
  formatearSubtotal4Dec,
  getGuiaItemsWithQuantity,
  roundSubtotal4,
  isSubproductoProduct,
} from '../../../utils/produccionGuiaCostos';
import {
  buildDistribucionPayload,
  calcularRestoEnOrigen,
  initDistribucionPorProducto,
  validarDistribucion,
} from '../../../utils/produccionGuiaDistribucion';

const fmtMoney = (n) => {
  const x = Number(n);
  const v = Number.isFinite(x) ? x : 0;
  return v.toLocaleString('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 2 });
};

function normalizarHoraHHmm(raw, fallback = '08:00') {
  const t = String(raw || '').trim();
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return fallback;
  const hh = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  const mm = Math.min(59, Math.max(0, parseInt(m[2], 10)));
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const STATUS_EN_PRODUCCION = 'en_produccion';

const GuiasProduccionAnalisis = () => {
  const { tipoSlug, id } = useParams();
  const config = getGuiaConfigBySlug(tipoSlug);
  const basePath = `/produccion/guias/${tipoSlug}`;
  const canMutate = useCanMutate();
  const { produccionFabricarPepDesdePtEnabled, guidesRemoveStockRestrictions } = useModuleSettings();

  const [guia, setGuia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recetasFullById, setRecetasFullById] = useState(() => new Map());
  const [recetasCargando, setRecetasCargando] = useState(false);
  const [recetasLoadProgress, setRecetasLoadProgress] = useState({ loaded: 0, total: 0 });
  const [catalogoListo, setCatalogoListo] = useState(false);
  const [stockCostByProduct, setStockCostByProduct] = useState({});
  const [productUnitById, setProductUnitById] = useState({});
  /** product_id → { linked_to_recipe, output_product_type } para tipar insumos vs subproductos */
  const [productTipoById, setProductTipoById] = useState({});
  /** product_id → id de receta PEP */
  const [pepRecetaIdByProductId, setPepRecetaIdByProductId] = useState({});
  /** product_id → ficha completa de receta PEP */
  const [pepRecetasByProductId, setPepRecetasByProductId] = useState(() => new Map());
  const [pepRecetasCargando, setPepRecetasCargando] = useState(false);
  const [pepRecetasLoadProgress, setPepRecetasLoadProgress] = useState({ loaded: 0, total: 0 });
  const [loadingKardex, setLoadingKardex] = useState(false);
  const [insumoDetalleModal, setInsumoDetalleModal] = useState(null);
  const [expandedRecetaId, setExpandedRecetaId] = useState(null);
  const [expandedPepProductId, setExpandedPepProductId] = useState(null);
  const [confirmSacarOpen, setConfirmSacarOpen] = useState(false);
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);
  const [tratarComoVenta, setTratarComoVenta] = useState(false);
  const [distribuirEnAlmacenes, setDistribuirEnAlmacenes] = useState(false);
  const [distribucionPorProducto, setDistribucionPorProducto] = useState({});
  const [warehousesAll, setWarehousesAll] = useState([]);
  const [distribucionError, setDistribucionError] = useState('');
  const [sacando, setSacando] = useState(false);
  const [revirtiendo, setRevirtiendo] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [pdfProgress, setPdfProgress] = useState('');

  const esProductoTerminado = config?.guideType === 'producto_terminado';

  const loadGuia = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const res = await produccionGuiaService.get(id);
      if (!res.success || !res.data) {
        setError(res.message || 'Guía no encontrada');
        setGuia(null);
        return;
      }
      setGuia(res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Error al cargar la guía');
      setGuia(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadGuia();
  }, [loadGuia]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await produccionGuiaService.formOptions();
        if (!cancel && res?.success && Array.isArray(res.data?.warehouses)) {
          setWarehousesAll(res.data.warehouses);
        }
      } catch {
        /* sin almacenes: distribución deshabilitada */
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const rows = guia?.rows || [];
  const itemsWithQty = useMemo(() => getGuiaItemsWithQuantity(rows), [rows]);

  const uniqueRecetaIds = useMemo(() => {
    const ids = new Set();
    for (const item of itemsWithQty) {
      if (item.receta_plato_id != null) ids.add(String(item.receta_plato_id));
    }
    return Array.from(ids);
  }, [itemsWithQty]);

  useEffect(() => {
    let cancel = false;
    if (!uniqueRecetaIds.length) {
      setRecetasFullById(new Map());
      setRecetasCargando(false);
      setRecetasLoadProgress({ loaded: 0, total: 0 });
      return undefined;
    }
    setRecetasCargando(true);
    setRecetasLoadProgress({ loaded: 0, total: uniqueRecetaIds.length });
    (async () => {
      const m = new Map();
      let loaded = 0;
      const bump = () => {
        loaded += 1;
        if (!cancel) setRecetasLoadProgress({ loaded, total: uniqueRecetaIds.length });
      };
      await Promise.all(
        uniqueRecetaIds.map(async (idStr) => {
          try {
            const res = await recetaPlatoService.get(idStr);
            if (res?.success && res.data) m.set(idStr, res.data);
          } catch {
            /* sin ficha: costo 0 */
          } finally {
            bump();
          }
        }),
      );
      if (!cancel) {
        setRecetasFullById(m);
        setRecetasCargando(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [uniqueRecetaIds]);

  useEffect(() => {
    let cancel = false;
    setCatalogoListo(false);
    (async () => {
      try {
        const [productsRes, recetasRes] = await Promise.all([
          productService.getProducts({ active: true }),
          recetaPlatoService.list({ active_only: true }).catch(() => null),
        ]);
        if (cancel) return;
        const productsRaw = Array.isArray(productsRes?.data)
          ? productsRes.data
          : Array.isArray(productsRes?.data?.data)
            ? productsRes.data.data
            : [];
        const unitsMap = {};
        const tipoMap = {};
        const pepIdMap = {};
        productsRaw.forEach((p) => {
          if (p?.id == null) return;
          const id = String(p.id);
          const u = String(p?.unit_of_measure ?? '').trim();
          if (u) unitsMap[id] = u;
          tipoMap[id] = {
            linked_to_recipe: Boolean(p.linked_to_recipe),
            output_product_type: null,
            is_insumo: Boolean(p.is_insumo),
          };
        });

        const recetasRaw = Array.isArray(recetasRes?.data)
          ? recetasRes.data
          : Array.isArray(recetasRes?.data?.data)
            ? recetasRes.data.data
            : [];
        recetasRaw.forEach((r) => {
          const register = (productId, outputType, recetaId) => {
            if (productId == null) return;
            const id = String(productId);
            const prev = tipoMap[id] || { linked_to_recipe: true, output_product_type: null, is_insumo: false };
            tipoMap[id] = {
              ...prev,
              linked_to_recipe: true,
              output_product_type: outputType || prev.output_product_type || null,
            };
            if (outputType === 'producto_en_proceso' && recetaId != null) {
              pepIdMap[id] = recetaId;
            }
          };

          register(r?.product_id, r.output_product_type, r.id);
          (r.outputs || []).forEach((out) => {
            register(out.product_id ?? out.finished_product?.id, out.output_product_type, r.id);
          });
        });

        setProductUnitById(unitsMap);
        setProductTipoById(tipoMap);
        setPepRecetaIdByProductId(pepIdMap);
        if (!cancel) setCatalogoListo(true);
      } catch {
        if (!cancel) {
          setProductUnitById({});
          setProductTipoById({});
          setPepRecetaIdByProductId({});
          setCatalogoListo(true);
        }
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const whId = guia?.warehouse_id != null ? String(guia.warehouse_id) : '';
  const ymd = guia?.guide_date || '';
  const horaCorte = normalizarHoraHHmm(guia?.guide_time, '08:00');

  useEffect(() => {
    if (!whId || !ymd) {
      setStockCostByProduct({});
      return undefined;
    }
    let cancel = false;
    setLoadingKardex(true);
    entryGuideService
      .getProductStockAndCostBulk(whId, null, ymd, horaCorte, true)
      .then((resp) => {
        if (cancel) return;
        if (resp?.success && resp?.data && typeof resp.data === 'object') {
          setStockCostByProduct(resp.data);
        } else {
          setStockCostByProduct({});
        }
      })
      .catch(() => {
        if (!cancel) setStockCostByProduct({});
      })
      .finally(() => {
        if (!cancel) setLoadingKardex(false);
      });
    return () => {
      cancel = true;
    };
  }, [whId, ymd, horaCorte]);

  const resumenInsumos = useMemo(
    () => buildResumenInsumosGuia(itemsWithQty, recetasFullById),
    [itemsWithQty, recetasFullById],
  );

  const subproductoIdsEnGuia = useMemo(() => {
    if (!esProductoTerminado) return [];
    const ids = new Set();
    for (const row of resumenInsumos.filas || []) {
      if (row?.product_id == null) continue;
      const pid = String(row.product_id);
      if (isSubproductoProduct(row.product_id, productTipoById) && pepRecetaIdByProductId[pid]) {
        ids.add(pid);
      }
    }
    return Array.from(ids).sort();
  }, [esProductoTerminado, resumenInsumos, productTipoById, pepRecetaIdByProductId]);

  useEffect(() => {
    let cancel = false;
    if (!esProductoTerminado || subproductoIdsEnGuia.length === 0) {
      setPepRecetasByProductId(new Map());
      setPepRecetasCargando(false);
      setPepRecetasLoadProgress({ loaded: 0, total: 0 });
      return undefined;
    }
    setPepRecetasCargando(true);
    setPepRecetasLoadProgress({ loaded: 0, total: subproductoIdsEnGuia.length });
    (async () => {
      const m = new Map();
      let loaded = 0;
      const bump = () => {
        loaded += 1;
        if (!cancel) setPepRecetasLoadProgress({ loaded, total: subproductoIdsEnGuia.length });
      };
      await Promise.all(
        subproductoIdsEnGuia.map(async (pid) => {
          const recetaId = pepRecetaIdByProductId[pid];
          if (recetaId == null) {
            bump();
            return;
          }
          try {
            const res = await recetaPlatoService.get(recetaId);
            if (res?.success && res.data) m.set(pid, res.data);
          } catch {
            /* sin ficha PEP */
          } finally {
            bump();
          }
        }),
      );
      if (!cancel) {
        setPepRecetasByProductId(m);
        setPepRecetasCargando(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [esProductoTerminado, subproductoIdsEnGuia, pepRecetaIdByProductId]);

  const explosionPep = useMemo(() => {
    if (!esProductoTerminado) {
      return {
        hasPep: false,
        insumosPep: { filas: [], totalCosto: 0 },
        insumosAgregados: { filas: [], totalCosto: 0 },
        resumenSubproductos: { rows: [], costTotal: 0 },
        resumenCoproductosPep: { rows: [], costTotal: 0 },
        pepUnitCostByProductId: {},
        desglosePep: new Map(),
        desgloseAgregados: new Map(),
      };
    }
    return buildExplosionPepAnalisis({
      itemsWithQty,
      recetasFullById,
      pepRecetasByProductId,
      stockCostByProduct,
      productUnitById,
      productTipoById,
      globalFabricarSubrecetaDefault: produccionFabricarPepDesdePtEnabled,
    });
  }, [
    esProductoTerminado,
    itemsWithQty,
    recetasFullById,
    pepRecetasByProductId,
    stockCostByProduct,
    productUnitById,
    productTipoById,
    produccionFabricarPepDesdePtEnabled,
  ]);

  const fabricarPepDesdePt = explosionPep.hasPep;

  const resumenInsumosKardexBase = useMemo(
    () => applyKardexToInsumoResumen(resumenInsumos, stockCostByProduct, productUnitById),
    [resumenInsumos, stockCostByProduct, productUnitById],
  );

  const resumenInsumosKardex = explosionPep.hasPep
    ? explosionPep.insumosAgregados
    : resumenInsumosKardexBase;

  const resumenInsumosPepKardex = explosionPep.hasPep
    ? explosionPep.insumosPep
    : { filas: [], totalCosto: 0 };

  /** product_id → cantidad que saldrá primero en fabricación PEP (misma unidad canónica). */
  const consumoPepByProductId = useMemo(() => {
    const map = new Map();
    for (const row of resumenInsumosPepKardex.filas || []) {
      if (row?.product_id == null) continue;
      const pid = String(row.product_id);
      map.set(pid, (map.get(pid) || 0) + Number(row.quantity || 0));
    }
    return map;
  }, [resumenInsumosPepKardex]);

  /** Insumos PT: stock disponible = kárdex − consumo PEP; co-productos PEP abonan cantidad y costo. */
  const resumenInsumosKardexPt = useMemo(() => {
    const filas = (resumenInsumosKardex.filas || []).map((row) => {
      if (row.esSubproductoCosteado || row.product_id == null) return row;
      const consumoPep = Number(consumoPepByProductId.get(String(row.product_id)) || 0);
      if (!(consumoPep > 0)) return row;
      const stockAntes = row.stockKardex == null ? null : Number(row.stockKardex);
      const stockDisponible =
        stockAntes == null || !Number.isFinite(stockAntes) ? null : stockAntes - consumoPep;
      return {
        ...row,
        stockAntesPep: stockAntes,
        consumoPep,
        stockKardex: stockDisponible,
        stockAjustadoPorPep: true,
      };
    });
    const base = { ...resumenInsumosKardex, filas };
    if (!explosionPep.hasPep || !(explosionPep.resumenCoproductosPep?.rows?.length > 0)) {
      return base;
    }
    return applyCoproductosToInsumoResumen(base, explosionPep.resumenCoproductosPep);
  }, [resumenInsumosKardex, consumoPepByProductId, explosionPep]);

  const resumenPlatos = useMemo(
    () =>
      buildResumenPlatosGuia(
        itemsWithQty,
        recetasFullById,
        stockCostByProduct,
        explosionPep.hasPep ? explosionPep.pepUnitCostByProductId : {},
      ),
    [itemsWithQty, recetasFullById, stockCostByProduct, explosionPep],
  );

  /** Subproductos recuperados de insumos (ej. pepas = % del bruto de papaya). */
  const resumenByproducts = useMemo(
    () => buildResumenByproductsFromInsumos(itemsWithQty, recetasFullById, productUnitById),
    [itemsWithQty, recetasFullById, productUnitById],
  );

  /** Mismo producto que se fabricará en esta guía (PEP, co-productos, recuperados). */
  const generandoseByProductId = useMemo(
    () =>
      buildGenerandoseByProductId({
        coproductosRows: explosionPep.resumenCoproductosPep?.rows || [],
        subproductosPepRows: explosionPep.resumenSubproductos?.rows || [],
        byproductRows: resumenByproducts.rows || [],
      }),
    [explosionPep, resumenByproducts],
  );

  const desgloseInsumoPorReceta = useMemo(() => {
    if (explosionPep.hasPep) return explosionPep.desgloseAgregados;
    return buildDesgloseInsumoPorReceta(itemsWithQty, recetasFullById, stockCostByProduct, productUnitById);
  }, [explosionPep, itemsWithQty, recetasFullById, stockCostByProduct, productUnitById]);

  const desgloseInsumoPepPorReceta = explosionPep.hasPep ? explosionPep.desglosePep : new Map();

  const toggleRecetaExpand = (recetaId) => {
    setExpandedRecetaId((prev) => (prev === recetaId ? null : recetaId));
  };

  const togglePepExpand = (productId) => {
    setExpandedPepProductId((prev) => (prev === productId ? null : productId));
  };

  const enProduccion = guia?.production_status === STATUS_EN_PRODUCCION;

  const platosParaInventario = useMemo(
    () => (resumenPlatos?.rows || []).filter((r) => r.product_id != null && Number(r.quantity) > 0),
    [resumenPlatos],
  );

  const warehousesDestino = useMemo(() => {
    const whId = guia?.warehouse_id != null ? Number(guia.warehouse_id) : null;
    return (warehousesAll || []).filter((w) => whId == null || Number(w.id) !== whId);
  }, [warehousesAll, guia?.warehouse_id]);

  useEffect(() => {
    if (confirmSacarOpen && platosParaInventario.length > 0) {
      setDistribucionPorProducto(initDistribucionPorProducto(platosParaInventario));
      setDistribucionError('');
    }
  }, [confirmSacarOpen, platosParaInventario]);

  const handleDistribucionQtyChange = (productId, warehouseId, value) => {
    setDistribucionPorProducto((prev) => {
      const pid = String(productId);
      const wh = String(warehouseId);
      const next = { ...prev, [pid]: { ...(prev[pid] || {}) } };
      if (value === '' || value == null) {
        next[pid][wh] = '';
      } else {
        const n = Number(value);
        next[pid][wh] = Number.isFinite(n) ? n : '';
      }
      return next;
    });
    setDistribucionError('');
  };

  const recetasSinProducto = useMemo(
    () => (resumenPlatos?.rows || []).filter((r) => !r.product_id && Number(r.quantity) > 0),
    [resumenPlatos],
  );

  const insumosSinStock = useMemo(() => {
    const rows = [];
    const seen = new Set();
    const pushIfShort = (row, origen) => {
      if (!row || row.esSubproductoCosteado) return;
      if (!insumoSinStockSuficiente(row, generandoseByProductId)) return;
      const key = `${row.product_id ?? row.product}|||${row.unit}`;
      if (seen.has(key)) return;
      seen.add(key);
      const { stock, generandose, disponible, bruto, despacho } = stockDisponibleInsumoGuia(
        row,
        generandoseByProductId,
      );
      rows.push({
        product: row.product,
        unit: row.unit,
        quantity: bruto,
        cantidadDespacho: despacho,
        generandose,
        stock,
        stockDisponible: disponible,
        stockAntesPep: row.stockAntesPep,
        consumoPep: row.consumoPep,
        origen,
      });
    };
    if (explosionPep.hasPep) {
      (resumenInsumosPepKardex.filas || []).forEach((r) => pushIfShort(r, 'PEP'));
      (resumenInsumosKardexPt.filas || []).forEach((r) => pushIfShort(r, 'PT'));
    } else {
      (resumenInsumosKardexPt.filas || []).forEach((r) => pushIfShort(r, 'PT'));
    }
    rows.sort((a, b) => String(a.product).localeCompare(String(b.product), 'es'));
    return rows;
  }, [explosionPep.hasPep, resumenInsumosPepKardex, resumenInsumosKardexPt, generandoseByProductId]);

  const bloqueadoPorStock = insumosSinStock.length > 0 && !guidesRemoveStockRestrictions;

  const cargaAnalisis = useMemo(() => {
    const recetasTotal = recetasLoadProgress.total || uniqueRecetaIds.length;
    const recetasLoaded = Math.min(recetasLoadProgress.loaded, recetasTotal);
    const pepTotal = pepRecetasLoadProgress.total;
    const pepLoaded = Math.min(pepRecetasLoadProgress.loaded, pepTotal);

    let totalWeight = 0;
    let doneWeight = 0;

    totalWeight += 1;
    if (catalogoListo) doneWeight += 1;

    if (recetasTotal > 0) {
      totalWeight += recetasTotal;
      doneWeight += recetasLoaded;
    }

    if (pepTotal > 0) {
      totalWeight += pepTotal;
      doneWeight += pepLoaded;
    }

    if (whId && ymd) {
      totalWeight += 1;
      if (!loadingKardex) doneWeight += 1;
    }

    const pct = totalWeight > 0 ? Math.min(100, Math.round((doneWeight / totalWeight) * 100)) : 100;
    const pendiente =
      !catalogoListo ||
      recetasCargando ||
      pepRecetasCargando ||
      loadingKardex ||
      (recetasTotal > 0 && recetasLoaded < recetasTotal) ||
      (pepTotal > 0 && pepLoaded < pepTotal);

    const faltanRecetas = Math.max(0, recetasTotal - recetasLoaded);
    const faltanPep = Math.max(0, pepTotal - pepLoaded);

    return {
      pct,
      pendiente,
      recetasLoaded,
      recetasTotal,
      faltanRecetas,
      pepLoaded,
      pepTotal,
      faltanPep,
      catalogoListo,
      kardexListo: !loadingKardex && Boolean(whId && ymd),
    };
  }, [
    catalogoListo,
    recetasLoadProgress,
    uniqueRecetaIds.length,
    pepRecetasLoadProgress,
    recetasCargando,
    pepRecetasCargando,
    loadingKardex,
    whId,
    ymd,
  ]);

  const puedeSacarAProduccion =
    canMutate &&
    !enProduccion &&
    itemsWithQty.length > 0 &&
    resumenInsumosKardexPt.filas.length > 0 &&
    platosParaInventario.length > 0 &&
    !recetasCargando &&
    !pepRecetasCargando &&
    !loadingKardex &&
    !bloqueadoPorStock;

  const handleSacarAProduccion = async () => {
    if (!id || !guia?.warehouse_id) return;

    let distribucionPayload = [];
    if (distribuirEnAlmacenes) {
      const validacion = validarDistribucion(platosParaInventario, distribucionPorProducto, warehousesDestino);
      if (!validacion.ok) {
        setDistribucionError(validacion.message);
        return;
      }
      distribucionPayload = buildDistribucionPayload(
        platosParaInventario,
        distribucionPorProducto,
        productUnitById,
      );
    }

    const filasInsumosPrecio = explosionPep.hasPep
      ? [
          ...resumenInsumosPepKardex.filas,
          ...resumenInsumosKardexPt.filas.filter((r) => !r.esSubproductoCosteado),
        ]
      : resumenInsumosKardexPt.filas;
    const insumos = filasInsumosPrecio
      .map((r) => {
        const despacho =
          r.cantidadDespacho != null ? Number(r.cantidadDespacho) : Number(r.quantity || 0);
        return { ...r, _despachoQty: despacho };
      })
      .filter((r) => r?.product_id != null && Number(r._despachoQty || 0) > 0)
      .map((r) => ({
        product_id: Number(r.product_id),
        description: r.product || null,
        project_id: guia.project_id ? Number(guia.project_id) : null,
        quantity: Number(r._despachoQty || 0),
        unit_of_measure: r.unit || null,
        unit_price: Number(r.costoUnitarioDespacho ?? r.costoUnitarioKardex ?? 0),
        condition: 'good',
      }));
    const platos = platosParaInventario.map((r) => {
      const pid = r.product_id != null ? String(r.product_id) : '';
      const canonicalUnit =
        (pid && productUnitById[pid]) ||
        (r.unit_of_measure && String(r.unit_of_measure).toLowerCase() === 'unidad' ? 'UN' : r.unit_of_measure) ||
        (r.output_unit_of_measure && String(r.output_unit_of_measure).toLowerCase() === 'unidad'
          ? 'UN'
          : r.output_unit_of_measure) ||
        'UN';
      return {
        product_id: Number(r.product_id),
        description: r.receta_name || null,
        project_id: guia.project_id ? Number(guia.project_id) : null,
        quantity: Number(r.quantity || 0),
        unit_of_measure: canonicalUnit,
        unit_price: Number(r.unitPrice || 0),
        condition: 'good',
      };
    });

    // Entrada adicional: subproductos recuperados de insumos (costo 0).
    // Líneas separadas (no fusionar con elaborados) para no distorsionar el costo unitario.
    for (const bp of resumenByproducts.rows || []) {
      if (!(Number(bp.quantity) > 0) || bp.product_id == null) continue;
      const pid = String(bp.product_id);
      platos.push({
        product_id: Number(bp.product_id),
        description: bp.product || 'Subproducto recuperado',
        project_id: guia.project_id ? Number(guia.project_id) : null,
        quantity: Number(bp.quantity || 0),
        unit_of_measure: bp.unit || productUnitById[pid] || 'UN',
        unit_price: 0,
        condition: 'good',
      });
    }

    for (const cp of explosionPep.resumenCoproductosPep?.rows || []) {
      if (!(Number(cp.quantity) > 0) || cp.product_id == null) continue;
      const pid = String(cp.product_id);
      platos.push({
        product_id: Number(cp.product_id),
        description: cp.product_name || `Co-producto de ${cp.source_pep_name || 'PEP'}`,
        project_id: guia.project_id ? Number(guia.project_id) : null,
        quantity: Number(cp.quantity || 0),
        unit_of_measure: cp.unit_of_measure || productUnitById[pid] || 'UN',
        unit_price: Number(cp.unitPrice || 0),
        condition: 'good',
      });
    }

    setSacando(true);
    try {
      const res = await produccionGuiaService.sacarAProduccion(id, {
        insumos,
        platos,
        tratar_como_venta: esProductoTerminado && tratarComoVenta && !distribuirEnAlmacenes,
        distribuir_en_almacenes: distribuirEnAlmacenes,
        distribucion: distribucionPayload,
      });
      if (res.success) {
        setConfirmSacarOpen(false);
        setTratarComoVenta(false);
        setDistribuirEnAlmacenes(false);
        setDistribucionPorProducto({});
        setDistribucionError('');
        let msg = `Sacado a producción. Salida insumos: ${res.data?.salida_guide_code || '—'}. Entrada elaborados: ${res.data?.entrada_guide_code || '—'}.`;
        if (res.data?.fabricar_pep) {
          msg = `Sacado a producción con PEP. PEP salida/entrada: ${res.data?.pep_salida_guide_code || '—'} / ${res.data?.pep_entrada_guide_code || '—'}. PT salida/entrada: ${res.data?.salida_guide_code || '—'} / ${res.data?.entrada_guide_code || '—'}.`;
        }
        if (res.data?.tratado_como_venta && res.data?.venta_guide_code) {
          msg += ` Salida venta: ${res.data.venta_guide_code}.`;
        }
        if (Array.isArray(res.data?.distribucion_guides) && res.data.distribucion_guides.length > 0) {
          const partes = res.data.distribucion_guides.map(
            (d) => `${d.warehouse_name || d.warehouse_id}: ${d.traspaso_code || 'GT'} → ${d.recepcion_code || 'GR'}`,
          );
          msg += ` Distribución: ${partes.join('; ')}.`;
        }
        setToast({ isOpen: true, message: msg, type: 'success' });
        await loadGuia();
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo sacar a producción', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || e.message || 'Error al sacar a producción',
        type: 'error',
      });
    } finally {
      setSacando(false);
    }
  };

  const handleRevertirSalida = async () => {
    if (!id) return;
    setRevirtiendo(true);
    try {
      const res = await produccionGuiaService.revertirSalidaProduccion(id);
      if (res.success) {
        setConfirmRevertOpen(false);
        setToast({ isOpen: true, message: 'Salida a producción revertida.', type: 'success' });
        await loadGuia();
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo revertir', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || e.message || 'Error al revertir',
        type: 'error',
      });
    } finally {
      setRevirtiendo(false);
    }
  };

  if (!config) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <p className="text-red-600">Tipo de guía no válido.</p>
        <Link to="/produccion/guias" className="text-teal-700 text-sm mt-2 inline-block hover:underline">
          Volver
        </Link>
      </div>
    );
  }

  if (loading) {
    return <div className="p-8 text-center text-gray-600">Cargando análisis…</div>;
  }

  if (error || !guia) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <p className="text-red-600">{error || 'Guía no encontrada'}</p>
        <Link to={basePath} className="text-teal-700 text-sm mt-2 inline-block hover:underline">
          ← Volver al listado
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/produccion" className="hover:text-teal-600">
          Producción
        </Link>
        <span className="mx-2">/</span>
        <Link to={basePath} className="hover:text-teal-600">
          {config.shortTitle}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Costos e insumos</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Costos e insumos de producción</h1>
          <p className="text-sm text-gray-600 mt-1">
            Guía <span className="font-mono font-medium text-gray-900">{guia.code}</span>
            {' · '}
            {guia.guide_date}
            {guia.guide_time ? ` ${guia.guide_time}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0 items-center">
          {itemsWithQty.length > 0 ? (
            <>
              <Link
                to={`${basePath}/imprimir/${id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Imprimir informe
              </Link>
              <ProduccionGuiaPdfButton
                guiaId={id}
                showLabel
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 hover:bg-red-100"
                onProgress={setPdfProgress}
                onSuccess={() => {
                  setPdfProgress('');
                  setToast({ isOpen: true, message: 'PDF descargado', type: 'success' });
                }}
                onError={(msg) => {
                  setPdfProgress('');
                  setToast({ isOpen: true, message: msg || 'Error al generar PDF', type: 'error' });
                }}
              />
            </>
          ) : null}
          {canMutate && !enProduccion ? (
            <Link
              to={`${basePath}/editar/${id}`}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Editar guía
            </Link>
          ) : (
            <Link
              to={`${basePath}/editar/${id}`}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Ver guía
            </Link>
          )}
          <Link
            to={basePath}
            className="rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-800 hover:bg-teal-100"
          >
            ← Listado
          </Link>
        </div>
      </div>

      {pdfProgress ? (
        <div className="mb-4 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm text-teal-900">
          {pdfProgress}
        </div>
      ) : null}

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm text-sm text-gray-700 space-y-1">
        <p>
          <strong className="text-gray-900">Almacén de salida:</strong>{' '}
          {guia.warehouse_name || '—'}
        </p>
        <p>
          <strong className="text-gray-900">Proyecto u operación:</strong>{' '}
          {guia.project_name || '—'}
          {guia.project_type === 'operacion' ? (
            <span className="text-gray-500 ml-1">(operación)</span>
          ) : null}
        </p>
        <p>
          <strong className="text-gray-900">Corte kárdex:</strong>{' '}
          {ymd} {horaCorte}
          {loadingKardex ? (
            <span className="text-amber-700 ml-2">(actualizando costos…)</span>
          ) : null}
        </p>
        {enProduccion ? (
          <p className="pt-2">
            <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-900">
              En producción
            </span>
            {guia.pep_salida_guide_code ? (
              <span className="ml-2 text-gray-700">
                PEP salida:{' '}
                {guia.pep_salida_guide_id ? (
                  <Link
                    to={`/exit-guides/${guia.pep_salida_guide_id}/edit`}
                    className="font-mono text-teal-800 hover:underline"
                  >
                    {guia.pep_salida_guide_code}
                  </Link>
                ) : (
                  <span className="font-mono">{guia.pep_salida_guide_code}</span>
                )}
              </span>
            ) : null}
            {guia.pep_entrada_guide_code ? (
              <span className="ml-2 text-gray-700">
                PEP entrada:{' '}
                {guia.pep_entrada_guide_id ? (
                  <Link
                    to={`/entry-guides/${guia.pep_entrada_guide_id}/edit`}
                    className="font-mono text-teal-800 hover:underline"
                  >
                    {guia.pep_entrada_guide_code}
                  </Link>
                ) : (
                  <span className="font-mono">{guia.pep_entrada_guide_code}</span>
                )}
              </span>
            ) : null}
            {guia.salida_guide_code ? (
              <span className="ml-2 text-gray-700">
                Salida:{' '}
                {guia.salida_guide_id ? (
                  <Link
                    to={`/exit-guides/${guia.salida_guide_id}/edit`}
                    className="font-mono text-teal-800 hover:underline"
                  >
                    {guia.salida_guide_code}
                  </Link>
                ) : (
                  <span className="font-mono">{guia.salida_guide_code}</span>
                )}
              </span>
            ) : null}
            {guia.entrada_guide_code ? (
              <span className="ml-2 text-gray-700">
                Entrada:{' '}
                {guia.entrada_guide_id ? (
                  <Link
                    to={`/entry-guides/${guia.entrada_guide_id}/edit`}
                    className="font-mono text-teal-800 hover:underline"
                  >
                    {guia.entrada_guide_code}
                  </Link>
                ) : (
                  <span className="font-mono">{guia.entrada_guide_code}</span>
                )}
              </span>
            ) : null}
            {guia.tratado_como_venta && guia.venta_guide_code ? (
              <span className="ml-2 text-gray-700">
                Venta:{' '}
                {guia.venta_guide_id ? (
                  <Link
                    to={`/exit-guides/${guia.venta_guide_id}/edit`}
                    className="font-mono text-teal-800 hover:underline"
                  >
                    {guia.venta_guide_code}
                  </Link>
                ) : (
                  <span className="font-mono">{guia.venta_guide_code}</span>
                )}
              </span>
            ) : null}
            {Array.isArray(guia.distribucion_guides) && guia.distribucion_guides.length > 0 ? (
              <span className="block mt-2 text-gray-700">
                <span className="font-medium text-gray-900">Distribución a otros almacenes:</span>
                <ul className="mt-1 space-y-0.5 pl-0 list-none">
                  {guia.distribucion_guides.map((d) => (
                    <li key={`${d.destination_warehouse_id}-${d.traspaso_guide_id}-${d.recepcion_guide_id}`}>
                      <span className="text-gray-600">
                        {d.destination_warehouse_name || `Almacén ${d.destination_warehouse_id}`}:
                      </span>{' '}
                      {d.traspaso_guide_code ? (
                        <>
                          Traspaso{' '}
                          {d.traspaso_guide_id ? (
                            <Link
                              to={`/transfer-guides/${d.traspaso_guide_id}/edit`}
                              className="font-mono text-teal-800 hover:underline"
                            >
                              {d.traspaso_guide_code}
                            </Link>
                          ) : (
                            <span className="font-mono">{d.traspaso_guide_code}</span>
                          )}
                        </>
                      ) : null}
                      {d.traspaso_guide_code && d.recepcion_guide_code ? <span className="text-gray-400"> · </span> : null}
                      {d.recepcion_guide_code ? (
                        <>
                          Recepción{' '}
                          {d.recepcion_guide_id ? (
                            <Link
                              to={`/reception-guides/${d.recepcion_guide_id}/edit`}
                              className="font-mono text-teal-800 hover:underline"
                            >
                              {d.recepcion_guide_code}
                            </Link>
                          ) : (
                            <span className="font-mono">{d.recepcion_guide_code}</span>
                          )}
                        </>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </span>
            ) : null}
            <span className="block text-xs text-gray-500 mt-1">
              {Array.isArray(guia.distribucion_guides) && guia.distribucion_guides.length > 0
                ? `Producción en ${guia.warehouse_name || 'el almacén de la guía'}; parte de los elaborados se distribuyó a otros almacenes (ver traspasos/recepciones).`
                : `Insumos y elaborados se movieron en el mismo almacén (${guia.warehouse_name || '—'}).`}
            </span>
          </p>
        ) : null}
      </div>

      {cargaAnalisis.pendiente ? (
        <div
          className="mb-6 rounded-lg border border-amber-200 bg-amber-50/90 p-4 shadow-sm"
          role="status"
          aria-live="polite"
          aria-label="Progreso de carga del análisis"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="text-sm font-medium text-amber-950">Cargando análisis de costos e insumos…</p>
            <span className="text-sm font-semibold tabular-nums text-amber-900">{cargaAnalisis.pct}%</span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-amber-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-amber-500 transition-[width] duration-300 ease-out"
              style={{ width: `${cargaAnalisis.pct}%` }}
            />
          </div>
          <ul className="mt-3 space-y-1 text-xs text-amber-900/90">
            <li className={cargaAnalisis.catalogoListo ? 'text-emerald-800' : ''}>
              {cargaAnalisis.catalogoListo ? '✓' : '○'} Catálogo de productos y tipos
            </li>
            {cargaAnalisis.recetasTotal > 0 ? (
              <li className={cargaAnalisis.faltanRecetas === 0 ? 'text-emerald-800' : ''}>
                {cargaAnalisis.faltanRecetas === 0 ? '✓' : '○'} Recetas de la guía:{' '}
                <strong>
                  {cargaAnalisis.recetasLoaded}/{cargaAnalisis.recetasTotal}
                </strong>
                {cargaAnalisis.faltanRecetas > 0
                  ? ` (faltan ${cargaAnalisis.faltanRecetas})`
                  : null}
              </li>
            ) : null}
            {cargaAnalisis.pepTotal > 0 ? (
              <li className={cargaAnalisis.faltanPep === 0 ? 'text-emerald-800' : ''}>
                {cargaAnalisis.faltanPep === 0 ? '✓' : '○'} Subproductos PEP:{' '}
                <strong>
                  {cargaAnalisis.pepLoaded}/{cargaAnalisis.pepTotal}
                </strong>
                {cargaAnalisis.faltanPep > 0 ? ` (faltan ${cargaAnalisis.faltanPep})` : null}
              </li>
            ) : null}
            {whId && ymd ? (
              <li className={cargaAnalisis.kardexListo ? 'text-emerald-800' : ''}>
                {cargaAnalisis.kardexListo ? '✓' : '○'} Costos y stock kárdex ({ymd} {horaCorte})
              </li>
            ) : null}
          </ul>
        </div>
      ) : cargaAnalisis.recetasTotal > 0 ? (
        <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-2 text-xs text-emerald-900">
          Análisis listo:{' '}
          {cargaAnalisis.recetasTotal === 1
            ? '1 receta cargada'
            : `${cargaAnalisis.recetasTotal} recetas cargadas`}
          {cargaAnalisis.pepTotal > 0
            ? ` · ${cargaAnalisis.pepTotal} subproducto${cargaAnalisis.pepTotal === 1 ? '' : 's'} PEP`
            : ''}
          .
        </div>
      ) : null}

      {itemsWithQty.length > 0 && canMutate ? (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-gray-700">
            {enProduccion ? (
              <span>
                La salida a producción ya fue confirmada. Puede revertirla para eliminar las guías de inventario
                generadas.
              </span>
            ) : (
              <span>
                Confirme para generar la <strong>guía de salida de insumos</strong> y la{' '}
                <strong>guía de entrada de elaborados</strong> en el mismo almacén.
                {esProductoTerminado ? (
                  <> Opcionalmente puede registrar además una <strong>salida por venta</strong>.</>
                ) : null}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {enProduccion ? (
              <button
                type="button"
                onClick={() => setConfirmRevertOpen(true)}
                disabled={revirtiendo}
                className="rounded-md border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-900 hover:bg-rose-100 disabled:opacity-50"
              >
                {revirtiendo ? 'Revirtiendo…' : 'Revertir salida a producción'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setTratarComoVenta(false);
                  setDistribuirEnAlmacenes(false);
                  setDistribucionError('');
                  setConfirmSacarOpen(true);
                }}
                disabled={!puedeSacarAProduccion || sacando}
                title={
                  bloqueadoPorStock
                    ? 'Hay insumos sin stock suficiente'
                    : undefined
                }
                className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sacando ? 'Procesando…' : 'Sacar a producción'}
              </button>
            )}
          </div>
        </div>
      ) : null}

      {recetasSinProducto.length > 0 && !enProduccion ? (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {recetasSinProducto.length} receta(s) sin producto vinculado en catálogo; no se incluirán en la guía de
          entrada hasta configurar el producto en la ficha de receta:{' '}
          {recetasSinProducto.map((r) => r.receta_name).join(', ')}
        </div>
      ) : null}

      {insumosSinStock.length > 0 && !enProduccion ? (
        <div className="mb-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          <p className="font-semibold">
            {bloqueadoPorStock
              ? 'No se puede sacar a producción: stock insuficiente'
              : 'Advertencia: stock insuficiente (puede forzar si la restricción está desactivada)'}
          </p>
          <ul className="mt-2 list-disc pl-5 space-y-0.5">
            {insumosSinStock.map((r) => (
              <li key={`${r.product}-${r.unit}-${r.origen}`}>
                <strong>{r.product}</strong> ({r.unit}): necesita{' '}
                {formatearCantidadReceta4Dec(r.quantity)}
                {Number(r.generandose || 0) > 0 ? (
                  <>
                    , stock{' '}
                    {Number(r.stock).toLocaleString('es-PE', { maximumFractionDigits: 3 })}
                    {' + '}
                    {Number(r.generandose).toLocaleString('es-PE', { maximumFractionDigits: 4 })}
                    {' generándose = '}
                    {Number(r.stockDisponible ?? 0).toLocaleString('es-PE', { maximumFractionDigits: 4 })}
                    {' disp.'}
                    {Number(r.cantidadDespacho || 0) > 0 ? (
                      <>
                        {' · despacho almacén '}
                        {formatearCantidadReceta4Dec(r.cantidadDespacho)}
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    , stock{' '}
                    {Number(r.stock).toLocaleString('es-PE', { maximumFractionDigits: 3 })}
                  </>
                )}
                {r.origen === 'PEP' ? ' · insumo de subproducto' : ''}
                {r.origen === 'PT' && r.consumoPep > 0
                  ? ` · tras PEP (${Number(r.stockAntesPep || 0).toLocaleString('es-PE', { maximumFractionDigits: 3 })} − ${Number(r.consumoPep).toLocaleString('es-PE', { maximumFractionDigits: 4 })})`
                  : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {itemsWithQty.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-900">
          Esta guía no tiene cantidades elaboradas. Indique al menos una cantidad en la guía para ver el análisis.
          <Link to={`${basePath}/editar/${id}`} className="block mt-2 font-medium text-teal-800 hover:underline">
            Ir a editar guía
          </Link>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <div className="rounded-lg border border-teal-200 bg-teal-50/60 p-4 max-w-sm">
              <p className="text-xs font-medium text-teal-800 uppercase tracking-wide">Costo total insumos</p>
              <p className="text-xl font-bold text-teal-900 mt-1">{fmtMoney(resumenInsumosKardexPt.totalCosto)}</p>
            </div>
          </div>

          <section className="mb-8">
            <h2 className="text-sm font-semibold text-gray-800 mb-2">Lo que se está produciendo</h2>
            <p className="text-xs text-gray-500 mb-3">
              Por cada receta: cantidad elaborada y costo promedio por unidad de salida (insumos × kárdex).
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <th className="px-3 py-2">Receta</th>
                    <th className="px-3 py-2">Clasificación</th>
                    <th className="px-3 py-2 text-right">Cant. elaborada</th>
                    <th className="px-3 py-2 text-right">Costo total</th>
                    <th className="px-3 py-2 text-right">Costo unitario</th>
                    <th className="px-3 py-2 text-center w-24">Insumos</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {resumenPlatos.rows.map((row) => {
                    const isOpen = expandedRecetaId === row.receta_plato_id;
                    const receta = recetasFullById.get(String(row.receta_plato_id));
                    return (
                      <React.Fragment key={row.receta_plato_id}>
                        <tr className="hover:bg-gray-50/80">
                          <td className="px-3 py-2">
                            <span className="font-medium text-gray-900">{row.receta_name}</span>
                            {row.receta_code ? (
                              <span className="block text-xs text-gray-500 font-mono">{row.receta_code}</span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-gray-600 text-xs max-w-[200px]">{row.classification}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatearCantidadReceta4Dec(row.quantity)}{' '}
                            <span className="text-gray-500 text-xs">{row.output_unit_of_measure}</span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(row.totalCost)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-teal-800">{formatearCostoUnitario4Dec(row.unitPrice)}</td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => toggleRecetaExpand(row.receta_plato_id)}
                              disabled={!receta}
                              className="text-xs font-medium text-teal-700 hover:underline disabled:text-gray-400"
                            >
                              {isOpen ? 'Ocultar' : 'Ver'}
                            </button>
                          </td>
                        </tr>
                        {isOpen && receta ? (
                          <tr className="bg-gray-50/90">
                            <td colSpan={6} className="px-4 py-3">
                              <DetalleRecetaInsumos
                                receta={receta}
                                qty={row.quantity}
                                stockCostByProduct={stockCostByProduct}
                                productUnitById={productUnitById}
                                productTipoById={productTipoById}
                                ingredientCostOverrides={
                                  explosionPep.hasPep ? explosionPep.pepUnitCostByProductId : {}
                                }
                              />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-teal-50/50 font-semibold">
                    <td colSpan={3} className="px-3 py-2 text-right text-gray-800">
                      Totales
                    </td>
                    <td colSpan={3} className="px-3 py-2 text-right text-teal-900">
                      {fmtMoney(resumenPlatos.costTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {resumenByproducts.rows.length > 0 ? (
            <section className="mb-8">
              <h2 className="text-sm font-semibold text-amber-900 mb-2">
                Subproductos recuperados de insumos
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Al sacar a producción entran a inventario junto con los elaborados (cantidad = % del bruto del
                insumo). Costo de entrada: 0.
              </p>
              <div className="overflow-x-auto rounded-lg border border-amber-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-amber-50 text-left text-xs uppercase text-amber-800">
                      <th className="px-3 py-2">Subproducto</th>
                      <th className="px-3 py-2">Origen (insumo)</th>
                      <th className="px-3 py-2 text-right">% recup.</th>
                      <th className="px-3 py-2 text-right">Cant. a ingresar</th>
                      <th className="px-3 py-2 text-right">Costo entrada</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {resumenByproducts.rows.map((row) => (
                      <tr key={`bp-${row.product_id}-${row.unit}`} className="hover:bg-amber-50/40">
                        <td className="px-3 py-2 font-medium text-gray-900">{row.product}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{row.source_product || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                          {Number(row.byproduct_pct || 0).toLocaleString('es-PE', {
                            maximumFractionDigits: 4,
                          })}{' '}
                          %
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatearCantidadReceta4Dec(row.quantity)}{' '}
                          <span className="text-gray-500 text-xs">{row.unit}</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtMoney(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {explosionPep.hasPep && explosionPep.resumenCoproductosPep?.rows?.length > 0 ? (
            <section className="mb-8">
              <h2 className="text-sm font-semibold text-emerald-900 mb-2">Co-productos de fabricación PEP</h2>
              <p className="text-xs text-gray-500 mb-3">
                Salidas adicionales de recetas con multi-salida al fabricar el producto en proceso (ej. tierra del
                keke). Entran a inventario con el % de costo asignado en la receta.
              </p>
              <div className="overflow-x-auto rounded-lg border border-emerald-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-emerald-50 text-left text-xs uppercase text-emerald-800">
                      <th className="px-3 py-2">Co-producto</th>
                      <th className="px-3 py-2">Origen (PEP)</th>
                      <th className="px-3 py-2 text-right">% costo</th>
                      <th className="px-3 py-2 text-right">Cant. a ingresar</th>
                      <th className="px-3 py-2 text-right">Costo unitario</th>
                      <th className="px-3 py-2 text-right">Costo total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {explosionPep.resumenCoproductosPep.rows.map((row) => (
                      <tr key={`cp-${row.product_id}-${row.unit_of_measure}`} className="hover:bg-emerald-50/40">
                        <td className="px-3 py-2 font-medium text-gray-900">{row.product_name}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">
                          {row.source_pep_name || row.receta_name || '—'}
                          {row.receta_code ? (
                            <span className="block font-mono text-gray-400">{row.receta_code}</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                          {row.cost_share_pct != null
                            ? `${Number(row.cost_share_pct).toLocaleString('es-PE', { maximumFractionDigits: 4 })} %`
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatearCantidadReceta4Dec(row.quantity)}{' '}
                          <span className="text-gray-500 text-xs">{row.unit_of_measure}</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-800">
                          {formatearCostoUnitario4Dec(row.unitPrice)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">
                          {fmtMoney(row.totalCost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-emerald-50/80 font-semibold">
                      <td colSpan={5} className="px-3 py-2 text-right text-emerald-900">
                        Totales
                      </td>
                      <td className="px-3 py-2 text-right text-emerald-900">
                        {fmtMoney(explosionPep.resumenCoproductosPep.costTotal)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          ) : null}

          {explosionPep.hasPep && explosionPep.resumenSubproductos.rows.length > 0 ? (
            <section className="mb-8">
              <h2 className="text-sm font-semibold text-violet-900 mb-2">Subproductos a fabricar</h2>
              <p className="text-xs text-gray-500 mb-3">
                Productos en proceso que se elaboran automáticamente al sacar a producción (costo = insumos de su
                receta × kárdex).
              </p>
              <div className="overflow-x-auto rounded-lg border border-violet-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-violet-50 text-left text-xs uppercase text-violet-700">
                      <th className="px-3 py-2">Subproducto</th>
                      <th className="px-3 py-2">Clasificación</th>
                      <th className="px-3 py-2 text-right">Cant. elaborada</th>
                      <th className="px-3 py-2 text-right">Costo total</th>
                      <th className="px-3 py-2 text-right">Costo unitario</th>
                      <th className="px-3 py-2 text-center w-24">Insumos</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {explosionPep.resumenSubproductos.rows.map((row) => {
                      const pid = row.product_id != null ? String(row.product_id) : null;
                      const isOpen = expandedPepProductId === pid;
                      const pepReceta = pid ? pepRecetasByProductId.get(pid) : null;
                      return (
                        <React.Fragment key={pid || row.product_name}>
                          <tr className="hover:bg-violet-50/40">
                            <td className="px-3 py-2">
                              <span className="font-medium text-gray-900">{row.product_name}</span>
                              {row.receta_code ? (
                                <span className="block text-xs text-gray-500 font-mono">{row.receta_code}</span>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-gray-600 text-xs max-w-[200px]">{row.classification}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {formatearCantidadReceta4Dec(row.quantity)}{' '}
                              <span className="text-gray-500 text-xs">{row.unit_of_measure}</span>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">
                              {fmtMoney(row.totalCost)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-violet-800">
                              {formatearCostoUnitario4Dec(row.unitPrice)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                type="button"
                                onClick={() => togglePepExpand(pid)}
                                disabled={!pepReceta}
                                className="text-xs font-medium text-violet-700 hover:underline disabled:text-gray-400"
                              >
                                {isOpen ? 'Ocultar' : 'Ver'}
                              </button>
                            </td>
                          </tr>
                          {isOpen && pepReceta ? (
                            <tr className="bg-violet-50/50">
                              <td colSpan={6} className="px-4 py-3">
                                <DetalleRecetaInsumos
                                  receta={pepReceta}
                                  qty={row.quantity}
                                  stockCostByProduct={stockCostByProduct}
                                  productUnitById={productUnitById}
                                  productTipoById={productTipoById}
                                />
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-violet-50/80 font-semibold">
                      <td colSpan={3} className="px-3 py-2 text-right text-violet-900">
                        Totales
                      </td>
                      <td colSpan={3} className="px-3 py-2 text-right text-violet-900">
                        {fmtMoney(explosionPep.resumenSubproductos.costTotal)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          ) : null}

          {explosionPep.hasPep ? (
            <section>
              <h2 className="text-sm font-semibold text-violet-900 mb-2">Insumos de subproductos</h2>
              <p className="text-xs text-gray-500 mb-3">
                Insumos hoja que se consumen al fabricar los subproductos (PEP) un minuto antes del producto
                terminado. Costo unitario desde kárdex al corte.
              </p>
              {resumenInsumosPepKardex.filas.length === 0 ? (
                <p className="text-sm text-gray-500">Sin insumos de subproductos.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-violet-200 bg-white shadow-sm">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-violet-50 text-left text-xs uppercase text-violet-700">
                        <th className="px-3 py-2">Producto / insumo</th>
                        <th className="px-3 py-2">Unidad</th>
                        <th className="px-3 py-2 text-right">Cantidad total</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">C. unit</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">Stock</th>
                        <th className="px-3 py-2 text-right">Subtotal est.</th>
                        <th className="px-3 py-2 text-center">Detalle</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {resumenInsumosPepKardex.filas.map((row, i) => {
                        const detailKey = row.product_id != null
                          ? `id:${String(row.product_id)}|||${row.unit}`
                          : `name:${row.product}|||${row.unit}`;
                        const detailRows = desgloseInsumoPepPorReceta.get(detailKey) || [];
                        const sinStock = insumoSinStockSuficiente(row, generandoseByProductId);
                        return (
                          <tr
                            key={`pep-${row.product}-${row.unit}-${i}`}
                            className={
                              sinStock
                                ? 'bg-rose-50 hover:bg-rose-100/80'
                                : 'hover:bg-violet-50/40'
                            }
                          >
                            <td className="px-3 py-2 text-gray-900">
                              <span className={sinStock ? 'font-semibold text-rose-900' : ''}>
                                {row.product}
                              </span>
                              {sinStock ? (
                                <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-200 text-rose-900">
                                  Sin stock
                                </span>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-gray-600">{row.unit}</td>
                            <td className={`px-3 py-2 text-right tabular-nums ${sinStock ? 'text-rose-900 font-semibold' : ''}`}>
                              {formatearCantidadReceta4Dec(row.quantity)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {formatearCostoUnitario4Dec(row.costoUnitarioKardex)}
                            </td>
                            <td className={`px-3 py-2 text-right tabular-nums ${sinStock ? 'text-rose-800 font-semibold' : 'text-gray-800'}`}>
                              {row.stockKardex != null
                                ? Number(row.stockKardex).toLocaleString('es-PE', { maximumFractionDigits: 3 })
                                : '—'}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatearSubtotal4Dec(row.subtotalKardex)}</td>
                            <td className="px-3 py-2 text-center">
                              <button
                                type="button"
                                onClick={() =>
                                  setInsumoDetalleModal({
                                    product: row.product,
                                    unit: row.unit,
                                    totalQuantity: row.quantity,
                                    totalCost: row.subtotalKardex,
                                    detailRows,
                                  })
                                }
                                disabled={detailRows.length === 0}
                                className="text-xs font-medium text-violet-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                              >
                                Ver receta
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-violet-50/80 font-semibold">
                        <td colSpan={5} className="px-3 py-2 text-right text-violet-900">
                          Total insumos de subproductos
                        </td>
                        <td className="px-3 py-2 text-right text-violet-900">
                          {fmtMoney(resumenInsumosPepKardex.totalCosto)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>
          ) : null}

          <section className={explosionPep.hasPep ? 'mt-8' : ''}>
            <h2 className="text-sm font-semibold text-gray-800 mb-2">Insumos agregados</h2>
            <p className="text-xs text-gray-500 mb-3">
              {explosionPep.hasPep
                ? 'Insumos directos (kárdex) y subproductos costeados. Bruto = neto + merma. Si hay co-producto PEP del mismo ítem, se abona cantidad y costo; el despacho real es la columna implícita (bruto − co-producto).'
                : 'Suma de insumos de las recetas. La cantidad es el bruto a despachar (neto + merma planificada). Costo unitario desde kárdex del almacén al corte indicado.'}
            </p>
            {resumenInsumosKardexPt.filas.length === 0 ? (
              <p className="text-sm text-gray-500">Sin insumos calculados.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <th className="px-3 py-2">Producto / insumo</th>
                      <th className="px-3 py-2">Tipo</th>
                      <th className="px-3 py-2">Unidad</th>
                      <th className="px-3 py-2 text-right">Neto</th>
                      <th className="px-3 py-2 text-right">Merma</th>
                      <th className="px-3 py-2 text-right">Bruto</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">C. unit</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">
                        {explosionPep.hasPep ? 'Stock disp.' : 'Stock'}
                      </th>
                      <th className="px-3 py-2 text-right">Subtotal est.</th>
                      <th className="px-3 py-2 text-center">Detalle</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {resumenInsumosKardexPt.filas.map((row, i) => {
                      const detailKey = row.product_id != null
                        ? `id:${String(row.product_id)}|||${row.unit}`
                        : `name:${row.product}|||${row.unit}`;
                      const detailRows = desgloseInsumoPorReceta.get(detailKey) || [];
                      const tipo = tipoInsumoLabel(row.product_id, productTipoById);
                      const sinStock =
                        !row.esSubproductoCosteado &&
                        insumoSinStockSuficiente(row, generandoseByProductId);
                      return (
                        <tr
                          key={`${row.product}-${row.unit}-${i}`}
                          className={sinStock ? 'bg-rose-50 hover:bg-rose-100/80' : 'hover:bg-gray-50/80'}
                        >
                          <td className="px-3 py-2 text-gray-900">
                            <span className={sinStock ? 'font-semibold text-rose-900' : ''}>
                              {row.product}
                            </span>
                            {sinStock ? (
                              <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-200 text-rose-900">
                                Sin stock
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                tipo === 'Subproducto'
                                  ? 'bg-violet-100 text-violet-800'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {tipo}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600">{row.unit}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                            {formatearCantidadReceta4Dec(row.quantity_net ?? row.quantity)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-amber-800">
                            {formatearCantidadReceta4Dec(row.quantity_merma || 0)}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums ${sinStock ? 'text-rose-900 font-semibold' : 'font-medium'}`}>
                            {formatearCantidadReceta4Dec(row.quantity)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatearCostoUnitario4Dec(row.costoUnitarioKardex)}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${sinStock ? 'text-rose-800 font-semibold' : 'text-gray-800'}`}>
                            {renderStockCeldaInsumo(row, generandoseByProductId)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatearSubtotal4Dec(row.subtotalKardex)}</td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() =>
                                setInsumoDetalleModal({
                                  product: row.product,
                                  unit: row.unit,
                                  totalQuantity: row.quantity,
                                  totalCost: row.subtotalKardex,
                                  detailRows,
                                })
                              }
                              disabled={detailRows.length === 0}
                              className="text-xs font-medium text-teal-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                            >
                              Ver receta
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-teal-50/50 font-semibold">
                      <td colSpan={8} className="px-3 py-2 text-right text-gray-800">
                        Total de costos {explosionPep.hasPep ? '(insumos + subproductos)' : '(kárdex al corte)'}
                      </td>
                      <td className="px-3 py-2 text-right text-teal-900">{fmtMoney(resumenInsumosKardexPt.totalCosto)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {confirmSacarOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-sacar-produccion-titulo"
          onClick={(e) => {
            if (e.target === e.currentTarget && !sacando) setConfirmSacarOpen(false);
          }}
        >
          <div
            className={`bg-white rounded-xl shadow-xl w-full border border-gray-200 ${
              distribuirEnAlmacenes ? 'max-w-4xl max-h-[90vh] flex flex-col' : 'max-w-lg'
            }`}
          >
            <div className="px-5 py-4 border-b border-gray-200 shrink-0">
              <h3 id="modal-sacar-produccion-titulo" className="text-lg font-semibold text-gray-900">
                Sacar a producción
              </h3>
            </div>
            <div
              className={`px-5 py-4 space-y-4 text-sm text-gray-700 ${
                distribuirEnAlmacenes ? 'overflow-y-auto flex-1' : ''
              }`}
            >
              <p>
                Se generarán en <strong>{guia.warehouse_name || 'el almacén de la guía'}</strong> (corte kárdex{' '}
                {ymd} {horaCorte}):
              </p>
              <ul className="list-disc pl-5 space-y-1 text-gray-600">
                {explosionPep.hasPep ? (
                  <>
                    <li>
                      Fabricación de <strong>subproductos (PEP)</strong>: salida de sus insumos y entrada del
                      subproducto (hora de la guía)
                    </li>
                    <li>
                      Luego (+1 min): guía de <strong>salida</strong> de insumos/subproductos y{' '}
                      <strong>entrada</strong> de elaborados
                    </li>
                  </>
                ) : (
                  <>
                    <li>Guía de <strong>salida</strong> de insumos (evento salida a producción)</li>
                    <li>Guía de <strong>entrada</strong> de productos elaborados (evento entrada a producción)</li>
                  </>
                )}
                {resumenByproducts.rows.length > 0 ? (
                  <li>
                    También entrarán <strong>subproductos recuperados</strong> de insumos (
                    {resumenByproducts.rows.length} producto
                    {resumenByproducts.rows.length === 1 ? '' : 's'}, costo 0)
                  </li>
                ) : null}
              </ul>
              {esProductoTerminado ? (
                <label className="flex items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tratarComoVenta}
                    onChange={(e) => setTratarComoVenta(e.target.checked)}
                    disabled={sacando || distribuirEnAlmacenes}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>
                    <span className="font-medium text-gray-900">Tratar elaborados como venta</span>
                    <span className="block text-xs text-gray-600 mt-1">
                      Además se creará una guía de <strong>salida por venta</strong> (evento venta, entidad Ventas) con
                      los mismos productos terminados y cantidades.
                      {distribuirEnAlmacenes ? (
                        <span className="block text-amber-700 mt-1">
                          No disponible mientras la distribución en otros almacenes está activa.
                        </span>
                      ) : null}
                    </span>
                  </span>
                </label>
              ) : null}
              {warehousesDestino.length > 0 ? (
                <label className="flex items-start gap-3 rounded-lg border border-teal-300 bg-teal-50/70 p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={distribuirEnAlmacenes}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setDistribuirEnAlmacenes(checked);
                      if (checked) setTratarComoVenta(false);
                      setDistribucionError('');
                    }}
                    disabled={sacando}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span>
                    <span className="font-medium text-gray-900">Distribuir elaborados en otros almacenes</span>
                    <span className="block text-xs text-gray-600 mt-1">
                      Tras la entrada en <strong>{guia.warehouse_name || 'el almacén de la guía'}</strong>, se generarán
                      automáticamente guías de <strong>traspaso (GT)</strong> y <strong>recepción (GR)</strong> emitidas
                      hacia los almacenes destino.
                    </span>
                  </span>
                </label>
              ) : null}
              {distribuirEnAlmacenes && warehousesDestino.length > 0 ? (
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <p className="px-3 py-2 bg-gray-50 text-xs text-gray-600 border-b border-gray-200">
                    Indique cuánto de cada elaborado se envía a cada almacén. Lo no asignado permanece en el almacén de
                    producción.
                  </p>
                  <div className="overflow-x-auto max-h-64">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-100 text-gray-700">
                        <tr>
                          <th className="px-2 py-2 text-left font-medium">Receta</th>
                          <th className="px-2 py-2 text-right font-medium">Producida</th>
                          {warehousesDestino.map((wh) => (
                            <th key={wh.id} className="px-2 py-2 text-right font-medium min-w-[5rem]">
                              {wh.name || wh.code}
                            </th>
                          ))}
                          <th className="px-2 py-2 text-right font-medium">Resto origen</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {platosParaInventario.map((row) => {
                          const pid = String(row.product_id);
                          const porAlmacen = distribucionPorProducto[pid] || {};
                          const producida = Number(row.quantity || 0);
                          const resto = calcularRestoEnOrigen(producida, porAlmacen);
                          const unit =
                            row.unit_of_measure ||
                            row.output_unit_of_measure ||
                            productUnitById[pid] ||
                            'UN';
                          return (
                            <tr key={pid} className="hover:bg-gray-50/80">
                              <td className="px-2 py-2 text-gray-900">
                                <span className="font-medium">{row.receta_name || '—'}</span>
                              </td>
                              <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                                {formatearCantidadReceta4Dec(producida)} {unit}
                              </td>
                              {warehousesDestino.map((wh) => (
                                <td key={wh.id} className="px-2 py-1 text-right">
                                  <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    disabled={sacando}
                                    value={porAlmacen[String(wh.id)] ?? ''}
                                    onChange={(e) =>
                                      handleDistribucionQtyChange(row.product_id, wh.id, e.target.value)
                                    }
                                    className="w-20 rounded border border-gray-300 px-1.5 py-1 text-right text-xs"
                                  />
                                </td>
                              ))}
                              <td className="px-2 py-2 text-right tabular-nums font-medium text-teal-800">
                                {formatearCantidadReceta4Dec(resto)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {distribucionError ? (
                    <p className="px-3 py-2 text-xs text-rose-700 bg-rose-50 border-t border-rose-200">
                      {distribucionError}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 shrink-0">
              <button
                type="button"
                disabled={sacando}
                onClick={() => setConfirmSacarOpen(false)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={sacando}
                onClick={handleSacarAProduccion}
                className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
              >
                {sacando ? 'Procesando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmRevertOpen}
        onClose={() => !revirtiendo && setConfirmRevertOpen(false)}
        onConfirm={handleRevertirSalida}
        title="Revertir salida a producción"
        message="Se eliminarán las guías de salida, entrada, venta (si aplica) y traspasos/recepciones de distribución vinculados. ¿Desea continuar?"
        confirmText={revirtiendo ? 'Revirtiendo…' : 'Revertir'}
        cancelText="Cancelar"
        type="danger"
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />

      {insumoDetalleModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setInsumoDetalleModal(null);
          }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 bg-teal-50/80">
              <h3 className="text-base font-semibold text-gray-900">
                Desglose por receta — {insumoDetalleModal.product}
              </h3>
              <p className="text-xs text-gray-600 mt-1">
                Unidad: {insumoDetalleModal.unit} · Cantidad total:{' '}
                {formatearCantidadReceta4Dec(insumoDetalleModal.totalQuantity)} · Costo total:{' '}
                {fmtMoney(insumoDetalleModal.totalCost)}
              </p>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1">
              {insumoDetalleModal.detailRows.length === 0 ? (
                <p className="text-sm text-gray-500">Sin detalle.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <th className="px-3 py-2">Receta</th>
                      <th className="px-3 py-2">Clasificación</th>
                      <th className="px-3 py-2 text-right">Cantidad</th>
                      <th className="px-3 py-2 text-right">C.unit</th>
                      <th className="px-3 py-2 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {insumoDetalleModal.detailRows.map((d, idx) => (
                      <tr key={`${d.receta}-${idx}`}>
                        <td className="px-3 py-2 text-gray-900">{d.receta}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{d.classification}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatearCantidadReceta4Dec(d.quantity)} {d.unit}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatearCostoUnitario4Dec(d.cUnit)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{formatearSubtotal4Dec(d.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                type="button"
                onClick={() => setInsumoDetalleModal(null)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function renderStockCeldaInsumo(row, generandoseByProductId) {
  if (row.esSubproductoCosteado) {
    return (
      <span className="inline-block text-violet-900" title="Stock kárdex + cantidad a fabricar">
        {Number(row.stockKardex ?? 0).toLocaleString('es-PE', { maximumFractionDigits: 3 })}
        {' + '}
        {Number(row.quantity || 0).toLocaleString('es-PE', { maximumFractionDigits: 4 })}
        <span className="block text-[10px] font-semibold text-violet-700">
          ={' '}
          {(Number(row.stockKardex ?? 0) + Number(row.quantity || 0)).toLocaleString('es-PE', {
            maximumFractionDigits: 4,
          })}
        </span>
      </span>
    );
  }

  const { stock, generandose, disponible, despacho } = stockDisponibleInsumoGuia(
    row,
    generandoseByProductId,
  );

  if (generandose > 0) {
    return (
      <span
        className="inline-block text-emerald-900"
        title="Stock kárdex + cantidad que se fabricará en esta guía"
      >
        {stock.toLocaleString('es-PE', { maximumFractionDigits: 3 })}
        {' + '}
        {generandose.toLocaleString('es-PE', { maximumFractionDigits: 4 })}
        <span className="block text-[10px] font-semibold text-emerald-800">
          = {disponible.toLocaleString('es-PE', { maximumFractionDigits: 4 })}
          <span className="font-normal text-gray-500"> disp.</span>
          {despacho > 0 ? (
            <>
              {' · despacho '}
              {despacho.toLocaleString('es-PE', { maximumFractionDigits: 4 })}
            </>
          ) : null}
        </span>
      </span>
    );
  }

  if (row.stockAjustadoPorPep) {
    return (
      <span
        className="inline-block text-amber-900"
        title="Stock kárdex menos salida PEP previa (mismo insumo)"
      >
        {Number(row.stockAntesPep ?? 0).toLocaleString('es-PE', { maximumFractionDigits: 3 })}
        {' − '}
        {Number(row.consumoPep || 0).toLocaleString('es-PE', { maximumFractionDigits: 4 })}
        <span className="block text-[10px] font-semibold text-amber-800">
          = {Number(row.stockKardex ?? 0).toLocaleString('es-PE', { maximumFractionDigits: 4 })}
          <span className="font-normal text-gray-500"> disp. PT</span>
        </span>
      </span>
    );
  }

  if (row.stockKardex != null) {
    return Number(row.stockKardex).toLocaleString('es-PE', { maximumFractionDigits: 3 });
  }

  return '—';
}

/** Clasifica un ítem de receta: Subproducto (tiene receta propia) vs Producto (insumo simple). */
function tipoInsumoLabel(productId, productTipoById) {
  if (productId == null) return 'Producto';
  const meta = productTipoById?.[String(productId)];
  if (!meta) return 'Producto';
  if (meta.output_product_type === 'producto_en_proceso' || meta.output_product_type === 'producto_terminado') {
    return 'Subproducto';
  }
  if (meta.linked_to_recipe) return 'Subproducto';
  return 'Producto';
}

function DetalleRecetaInsumos({
  receta,
  qty,
  stockCostByProduct,
  productUnitById,
  productTipoById,
  ingredientCostOverrides = {},
}) {
  const ings = lineasInsumoEscaladosRecetaApi(receta, qty);
  const rows = ings.map((row) => {
    const pid = row.product_id != null ? String(row.product_id) : null;
    const override =
      pid != null && ingredientCostOverrides[pid] != null ? Number(ingredientCostOverrides[pid]) : null;
    const cached = pid ? (stockCostByProduct?.[pid] || stockCostByProduct?.[row.product_id]) : null;
    const canonicalUnit = pid ? (productUnitById?.[pid] || row.unit || '') : (row.unit || '');
    const cUnit =
      override != null && Number.isFinite(override)
        ? override
        : Number(cached?.average_cost ?? row.precioUnitario ?? 0);
    const safe = Number.isFinite(cUnit) ? cUnit : 0;
    const subtotal = roundSubtotal4(Number(row.quantity || 0) * safe);
    const tipo = tipoInsumoLabel(row.product_id, productTipoById);
    return { ...row, unit: canonicalUnit || row.unit, cUnit: safe, subtotal, tipo };
  });
  const total = roundSubtotal4(rows.reduce((a, r) => a + r.subtotal, 0));

  return (
    <div className="text-sm">
      <p className="text-xs text-gray-500 mb-2">
        Insumos para <strong>{formatearCantidadReceta4Dec(qty)}</strong> unidad(es) elaborada(s) · Costo total:{' '}
        <strong>{fmtMoney(total)}</strong>
        <span className="block mt-0.5 text-[11px] text-gray-400">
          Cantidades: neto de fórmula, merma planificada y bruto a despachar (neto + merma). El subtotal usa el bruto.
        </span>
      </p>
      <table className="min-w-full text-xs border border-gray-200 rounded-md overflow-hidden">
        <thead>
          <tr className="bg-gray-100 text-gray-600">
            <th className="px-2 py-1 text-left">Insumo</th>
            <th className="px-2 py-1 text-left">Tipo</th>
            <th className="px-2 py-1 text-left">Unidad</th>
            <th className="px-2 py-1 text-right">Neto</th>
            <th className="px-2 py-1 text-right">% merma</th>
            <th className="px-2 py-1 text-right">Merma</th>
            <th className="px-2 py-1 text-right">Bruto</th>
            <th className="px-2 py-1 text-right">C.unit</th>
            <th className="px-2 py-1 text-right">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <React.Fragment key={i}>
              <tr className="border-t border-gray-100">
                <td className="px-2 py-1">{r.product}</td>
                <td className="px-2 py-1">
                  <span
                    className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      r.tipo === 'Subproducto'
                        ? 'bg-violet-100 text-violet-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {r.tipo}
                  </span>
                </td>
                <td className="px-2 py-1 text-gray-600">{r.unit}</td>
                <td className="px-2 py-1 text-right tabular-nums text-gray-700">
                  {formatearCantidadReceta4Dec(r.quantity_net ?? r.quantity)}
                </td>
                <td className="px-2 py-1 text-right tabular-nums text-amber-800">
                  {Number(r.merma_pct || 0) > 0
                    ? `${Number(r.merma_pct).toLocaleString('es-PE', { maximumFractionDigits: 4 })} %`
                    : '—'}
                </td>
                <td className="px-2 py-1 text-right tabular-nums text-amber-800">
                  {formatearCantidadReceta4Dec(r.quantity_merma || 0)}
                </td>
                <td className="px-2 py-1 text-right tabular-nums font-medium">
                  {formatearCantidadReceta4Dec(r.quantity)}
                </td>
                <td className="px-2 py-1 text-right tabular-nums">{formatearCostoUnitario4Dec(r.cUnit)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{formatearSubtotal4Dec(r.subtotal)}</td>
              </tr>
              {r.byproduct_product_id && Number(r.byproduct_quantity) > 0 ? (
                <tr className="bg-amber-50/70 border-t border-amber-100">
                  <td colSpan={9} className="px-2 py-1 text-[11px] text-amber-900">
                    Recupera{' '}
                    <strong>
                      {formatearCantidadReceta4Dec(r.byproduct_quantity)} {r.byproduct_unit || r.unit}
                    </strong>{' '}
                    de <strong>{r.byproduct_name || `Producto #${r.byproduct_product_id}`}</strong> (
                    {Number(r.byproduct_pct || 0).toLocaleString('es-PE', { maximumFractionDigits: 4 })} % del
                    bruto)
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default GuiasProduccionAnalisis;
